import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ArrowLeft, Sparkles, TestTube, Crown, CalendarClock, Trash2, Check, Bell, Pill, HeartHandshake, Scale, Sun } from "lucide-react";
import { toast } from "sonner";
import PushToggle from "@/components/PushToggle";
import EmailPrefsToggle from "@/components/EmailPrefsToggle";

const META = {
  diet_update: { label: "Dieta", icon: Sparkles },
  blood_reminder: { label: "Analisi", icon: TestTube },
  subscription: { label: "Abbonamento", icon: Crown },
  weekly_summary: { label: "Riepilogo", icon: CalendarClock },
  supplements: { label: "Integratori", icon: Pill },
  coach: { label: "Coach", icon: HeartHandshake },
  weight_reminder: { label: "Peso", icon: Scale },
  daily_reminder: { label: "Giornaliero", icon: Sun },
};

const FILTERS = [
  { v: "all", label: "Tutte" },
  { v: "diet_update", label: "Dieta" },
  { v: "supplements", label: "Integratori" },
  { v: "blood_reminder", label: "Analisi" },
  { v: "coach", label: "Coach" },
  { v: "weekly_summary", label: "Riepilogo" },
  { v: "subscription", label: "Abbonamento" },
];

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
    } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((n) => n.type === filter)),
    [items, filter]
  );
  const readCount = items.filter((n) => n.read).length;
  const unreadCount = items.filter((n) => !n.read).length;

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((arr) => arr.map((n) => (n.notif_id === id ? { ...n, read: true } : n)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems((arr) => arr.filter((n) => n.notif_id !== id));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const clearRead = async () => {
    try {
      const { data } = await api.post("/notifications/delete-read");
      setItems((arr) => arr.filter((n) => !n.read));
      toast.success(`${data.deleted} notifiche eliminate`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="notif-back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <img src="/DietFlix-logo.webp" alt="DietFlix" className="h-8" />
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <PushToggle />
        <EmailPrefsToggle />
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Bell className="h-6 w-6 text-primary" /> Centro notifiche</h1>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={markAllRead} disabled={unreadCount === 0} data-testid="notif-mark-all-read">
              <Check className="h-4 w-4 mr-2" /> Segna lette ({unreadCount})
            </Button>
            <Button variant="outline" size="sm" onClick={clearRead} disabled={readCount === 0} data-testid="notif-clear-read">
              <Trash2 className="h-4 w-4 mr-2" /> Elimina lette ({readCount})
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`notif-filter-${f.v}`}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === f.v ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:border-primary/50"}`}>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="notif-page-empty">Nessuna notifica</div>
        ) : (
          <div className="space-y-3" data-testid="notif-list">
            {filtered.map((n) => {
              const m = META[n.type] || { label: "Notifica", icon: Sparkles };
              const Ic = m.icon;
              return (
                <Card key={n.notif_id} className={`p-4 flex gap-4 ${n.read ? "opacity-70" : ""}`} data-testid={`notif-row-${n.notif_id}`}>
                  <div className="mt-0.5"><Ic className="h-5 w-5 text-primary shrink-0" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{n.title}</span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{m.label}</span>
                      {!n.read && <span className="h-2 w-2 rounded-full bg-accent" data-testid={`notif-unread-${n.notif_id}`} />}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{n.message}</p>
                    <div className="text-[11px] text-muted-foreground mt-1 font-mono-data">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {!n.read && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => markRead(n.notif_id)} data-testid={`notif-read-${n.notif_id}`} title="Segna come letta">
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive disabled:opacity-30" onClick={() => remove(n.notif_id)}
                      disabled={!n.read} data-testid={`notif-delete-${n.notif_id}`} title={n.read ? "Elimina" : "Segna prima come letta"}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
